/**
 * Site Health → Google Business Profile AUDIT.
 *
 * A different question from the visibility scan. The scan asks "where does
 * this client rank"; the audit asks "is the profile itself any good" — name,
 * address and phone against the website, description and categories,
 * attributes, photos, and unanswered reviews.
 *
 * Backend contract:
 *   GET  /site-health/gbp-audit?tenant_id=&place_id=&force=
 *   GET  /site-health/gbp-audit/history?tenant_id=&place_id=&limit=
 *   GET  /site-health/gbp-audit/compare?tenant_id=&run_id=&compare_to=
 *   POST /site-health/gbp-review-reply
 *
 * The reads cost NOTHING — the vendor's profile endpoints moved the credit
 * balance by zero when measured on 2026-09-14 — which is why this may run on
 * a page load while a scan may not.
 *
 * `place_id` is sent but never trusted: the server honours it only when it
 * names one of this tenant's own listings.
 */
import { apiClient } from "./apiClient";

export type GbpFindingSeverity = "critical" | "warning" | "info";

export type GbpFindingCategory =
	| "nap"
	| "profile"
	| "attributes"
	| "photos"
	| "reviews";

export interface GbpAuditFinding {
	/** Stable identity. History diffs join on this, not on the title. */
	code: string;
	category: GbpFindingCategory | string;
	severity: GbpFindingSeverity | string;
	title: string;
	detail: string;
	/** What Google says. Empty when the check had nothing to show. */
	gbpValue: string;
	/** What we hold. Empty means "not on file", NOT "blank on the website". */
	siteValue: string;
}

/**
 * A checklist item's state. The distinctions are the point — see the backend
 * module docstring. In particular `unknown` is NOT a pass: it means we could
 * not look, and rendering it green would be a lie to whoever signs the work off.
 */
export type GbpChecklistStatus =
	| "pass"
	| "fail"
	| "unknown"
	| "manual"
	| "unavailable"
	| "dismissed"
	/** An operator vouched for it. Distinct from `pass`, which is OUR check. */
	| "confirmed"
	/** An info-severity finding: worth saying, not a fault. Can be signed off. */
	| "minor";

export interface GbpChecklistDecision {
	status: "approved" | "declined" | string;
	actor: string;
	note: string;
	decidedAt: string | null;
}

/** One observed fact behind a check. `href` makes it verifiable in a click. */
export interface GbpEvidenceFact {
	label: string;
	value: string;
	href?: string;
}

/** The live profile as Google holds it, laid out the way Google groups it. */
export interface GbpProfileSnapshot {
	title: string;
	phone: string;
	address: string;
	website: string;
	description: string;
	primaryCategory: string;
	secondaryCategories: string[];
	hours: { day: string; hours: string }[];
	serviceArea: string[];
	status: string;
	/** Link to the live listing on Google Maps — the strongest proof there is. */
	mapsUri: string;
}

export interface GbpChecklistItem {
	id: string;
	section: string;
	title: string;
	/** auto = we check it; manual = a person must; unavailable = impossible. */
	mode: "auto" | "manual" | "unavailable" | string;
	/** Why it is manual or unavailable. Empty for automatic items. */
	note: string;
	status: GbpChecklistStatus;
	/** Present only on a failing automatic item. */
	finding: GbpAuditFinding | null;
	/**
	 * What was observed — shown on PASSING rows too, so a pass can be trusted
	 * without leaving the tool. Comes from the run it belongs to; never
	 * recomputed from today's profile for an older audit.
	 */
	evidence: GbpEvidenceFact[];
	decision: GbpChecklistDecision | null;
}

export interface GbpChecklistCounts {
	pass: number;
	fail: number;
	unknown: number;
	manual: number;
	unavailable: number;
	dismissed: number;
	confirmed: number;
}

export interface GbpPendingReview {
	reviewId: string;
	rating: number | null;
	comment: string;
	reviewer: string;
	dateCreated: string;
}

/** A review the client has already responded to. The reply is the point. */
export interface GbpAnsweredReview extends GbpPendingReview {
	reply: string;
	replyDate: string;
	/**
	 * Where Google has got to with this reply: PENDING, APPROVED or REJECTED.
	 *
	 * Empty means we do not know — not that it is live. Only some reading
	 * sources report it, so an empty value must render as nothing at all
	 * rather than as a reassuring "published".
	 */
	replyState: string;
}

export interface PostReplyParams {
	tenantId: string;
	reviewId: string;
	reply: string;
	placeId?: string;
	acknowledgedClaims?: boolean;
}

export interface GbpPostReplyResult {
	ok: boolean;
	/** Google ACCEPTED it. Not the same as publicly visible yet. */
	posted: boolean;
	reason: string;
	/** Assertions the operator must confirm before this will send. */
	claims: string[];
}

export interface GbpAuditCounts {
	critical: number;
	warning: number;
	info: number;
}

export interface GbpAuditDiff {
	new: GbpAuditFinding[];
	resolved: GbpAuditFinding[];
	unchanged: GbpAuditFinding[];
}

export interface GbpAuditResult {
	tenantId: string;
	/** False when no Local Falcon key is stored — NOT the same as "all clear". */
	connected: boolean;
	/** False when connected but this client has no listing assigned yet. */
	mapped: boolean;
	/**
	 * False when the client has never connected their Google account inside
	 * Local Falcon. Nothing is broken: attributes, photos and reviews simply
	 * cannot be read, while name/phone/website still are.
	 */
	gbpLinked: boolean;
	/** False when the profile read FAILED. Distinct from "profile is clean". */
	readOk: boolean;
	error: string | null;
	note: string | null;
	placeId: string;
	locationName: string;
	findings: GbpAuditFinding[];
	worstSeverity: GbpFindingSeverity | string;
	counts: GbpAuditCounts;
	summary: string;
	/** Already normalised — components must not re-shape it. */
	profile: GbpProfileSnapshot;
	reviewSummary: Record<string, unknown>;
	pendingReviews: GbpPendingReview[];
	/** Reviews already responded to, most recent reply first. */
	answeredReviews: GbpAnsweredReview[];
	photoCount: number;
	attributeCount: number;
	/** Every checklist item with its state — not only the failures. */
	checklist: GbpChecklistItem[];
	checklistCounts: GbpChecklistCounts;
	/** section key -> human title, in the order the checklist declares them. */
	sections: Record<string, string>;
	generatedAt: string | null;
	runId: string | null;
	comparedTo: string | null;
	diff: Partial<GbpAuditDiff>;
	/** True when a stored run inside the reuse window was returned as-is. */
	reused: boolean;
}

export interface GbpAuditHistoryRun {
	runId: string;
	placeId: string;
	locationName: string;
	/** Failed runs are LISTED but never compared — see the backend migration. */
	readOk: boolean;
	gbpLinked: boolean;
	worstSeverity: string;
	criticalCount: number;
	warningCount: number;
	infoCount: number;
	photoCount: number;
	attributeCount: number;
	reviewSummary: Record<string, unknown>;
	summary: string;
	generatedAt: string | null;
	newCount: number;
	resolvedCount: number;
}

/** Signed movement in one rank metric, with the direction already judged. */
export interface GbpScanDelta {
	/** null when there was no comparable earlier scan. */
	change: number | null;
	/**
	 * null means "no verdict" — either nothing to compare, or no change.
	 * ARP/ATRP improve by going DOWN and SoLV by going UP, so the backend
	 * decides this; the UI must not re-derive it from the sign.
	 */
	improved: boolean | null;
}

export interface GbpScan {
	reportKey: string;
	keyword: string;
	gridSize: number | null;
	radius: string;
	measurement: string;
	/** Average rank position — LOWER is better. null = not supplied. */
	arp: number | null;
	/** Average total rank position — LOWER is better. */
	atrp: number | null;
	/** Share of local voice, a percentage — HIGHER is better. */
	solv: number | null;
	foundIn: number | null;
	dataPoints: number | null;
	scannedAt: string | null;
	deltas: Record<"arp" | "atrp" | "solv", GbpScanDelta>;
	/** The earlier scan this was compared with, or null when incomparable. */
	comparedTo: string | null;
}

export interface GbpScanHistory {
	connected: boolean;
	mapped: boolean;
	scans: GbpScan[];
	error: string | null;
}

type RawObject = Record<string, unknown>;

const asString = (value: unknown): string =>
	typeof value === "string" ? value : "";

const asNumber = (value: unknown): number =>
	typeof value === "number" && Number.isFinite(value) ? value : 0;

const normalizeFinding = (raw: unknown): GbpAuditFinding => {
	const obj = (raw ?? {}) as RawObject;
	return {
		code: asString(obj.code),
		category: asString(obj.category) || "profile",
		severity: asString(obj.severity) || "info",
		title: asString(obj.title),
		detail: asString(obj.detail),
		gbpValue: asString(obj.gbp_value),
		siteValue: asString(obj.site_value),
	};
};

const normalizeFindings = (raw: unknown): GbpAuditFinding[] =>
	Array.isArray(raw) ? raw.map(normalizeFinding).filter((f) => f.code) : [];

const normalizeReview = (raw: unknown): GbpPendingReview => {
	const obj = (raw ?? {}) as RawObject;
	return {
		reviewId: asString(obj.review_id),
		rating: typeof obj.rating === "number" ? obj.rating : null,
		comment: asString(obj.comment),
		reviewer: asString(obj.reviewer),
		dateCreated: asString(obj.date_created),
	};
};

const normalizeAnsweredReview = (raw: unknown): GbpAnsweredReview => {
	const obj = (raw ?? {}) as RawObject;
	return {
		...normalizeReview(raw),
		reply: asString(obj.reply),
		replyDate: asString(obj.reply_date),
		replyState: asString(obj.reply_state).toUpperCase(),
	};
};

const normalizeCounts = (raw: unknown): GbpAuditCounts => {
	const obj = (raw ?? {}) as RawObject;
	return {
		critical: asNumber(obj.critical),
		warning: asNumber(obj.warning),
		info: asNumber(obj.info),
	};
};

const normalizeFact = (raw: unknown): GbpEvidenceFact => {
	const obj = (raw ?? {}) as RawObject;
	const href = asString(obj.href);
	return {
		label: asString(obj.label),
		value: asString(obj.value),
		...(href ? { href } : {}),
	};
};

const normalizeProfileSnapshot = (raw: unknown): GbpProfileSnapshot => {
	const obj = (raw ?? {}) as RawObject;
	const hours = Array.isArray(obj.hours) ? obj.hours : [];
	return {
		title: asString(obj.title),
		phone: asString(obj.phone),
		address: asString(obj.address),
		website: asString(obj.website),
		description: asString(obj.description),
		primaryCategory: asString(obj.primary_category),
		secondaryCategories: Array.isArray(obj.secondary_categories)
			? obj.secondary_categories.map(asString).filter(Boolean)
			: [],
		hours: hours
			.map((h) => {
				const row = (h ?? {}) as RawObject;
				return { day: asString(row.day), hours: asString(row.hours) };
			})
			.filter((h) => h.day),
		serviceArea: Array.isArray(obj.service_area)
			? obj.service_area.map(asString).filter(Boolean)
			: [],
		status: asString(obj.status),
		mapsUri: asString(obj.maps_uri),
	};
};

const normalizeDecision = (raw: unknown): GbpChecklistDecision | null => {
	if (!raw || typeof raw !== "object") return null;
	const obj = raw as RawObject;
	return {
		status: asString(obj.status),
		actor: asString(obj.actor),
		note: asString(obj.note),
		decidedAt: asString(obj.decided_at) || null,
	};
};

const normalizeChecklistItem = (raw: unknown): GbpChecklistItem => {
	const obj = (raw ?? {}) as RawObject;
	return {
		id: asString(obj.id),
		section: asString(obj.section),
		title: asString(obj.title),
		mode: asString(obj.mode) || "auto",
		note: asString(obj.note),
		// An unrecognised status must never fall back to "pass".
		status: (asString(obj.status) || "unknown") as GbpChecklistStatus,
		finding: obj.finding ? normalizeFinding(obj.finding) : null,
		evidence: Array.isArray(obj.evidence)
			? obj.evidence.map(normalizeFact).filter((f) => f.value)
			: [],
		decision: normalizeDecision(obj.decision),
	};
};

const normalizeChecklistCounts = (raw: unknown): GbpChecklistCounts => {
	const obj = (raw ?? {}) as RawObject;
	return {
		pass: asNumber(obj.pass),
		fail: asNumber(obj.fail),
		unknown: asNumber(obj.unknown),
		manual: asNumber(obj.manual),
		unavailable: asNumber(obj.unavailable),
		dismissed: asNumber(obj.dismissed),
		confirmed: asNumber(obj.confirmed),
	};
};

const normalizeResult = (raw: unknown): GbpAuditResult => {
	const obj = (raw ?? {}) as RawObject;
	const diff = (obj.diff ?? {}) as RawObject;
	return {
		tenantId: asString(obj.tenant_id),
		connected: Boolean(obj.connected),
		mapped: Boolean(obj.mapped),
		// Absent means linked — an older backend that predates the field must
		// not make every location look disconnected.
		gbpLinked: obj.gbp_linked === undefined ? true : Boolean(obj.gbp_linked),
		readOk: obj.read_ok === undefined ? true : Boolean(obj.read_ok),
		error: asString(obj.error) || null,
		note: asString(obj.note) || null,
		placeId: asString(obj.place_id),
		locationName: asString(obj.location_name),
		findings: normalizeFindings(obj.findings),
		worstSeverity: asString(obj.worst_severity) || "info",
		counts: normalizeCounts(obj.counts),
		summary: asString(obj.summary),
		profile: normalizeProfileSnapshot(obj.profile),
		reviewSummary: (obj.review_summary ?? {}) as Record<string, unknown>,
		pendingReviews: Array.isArray(obj.pending_reviews)
			? obj.pending_reviews.map(normalizeReview)
			: [],
		// No .filter() on either list. The checklist normaliser drops items
		// without an id; doing that here would silently lose reviews, which
		// is the exact failure this feature exists to end.
		answeredReviews: Array.isArray(obj.answered_reviews)
			? obj.answered_reviews.map(normalizeAnsweredReview)
			: [],
		photoCount: asNumber(obj.photo_count),
		attributeCount: asNumber(obj.attribute_count),
		checklist: Array.isArray(obj.checklist)
			? obj.checklist.map(normalizeChecklistItem).filter((i) => i.id)
			: [],
		checklistCounts: normalizeChecklistCounts(obj.checklist_counts),
		sections: (obj.sections ?? {}) as Record<string, string>,
		generatedAt: asString(obj.generated_at) || null,
		runId: asString(obj.run_id) || null,
		comparedTo: asString(obj.compared_to) || null,
		diff: {
			new: normalizeFindings(diff.new),
			resolved: normalizeFindings(diff.resolved),
			unchanged: normalizeFindings(diff.unchanged),
		},
		reused: Boolean(obj.reused),
	};
};

const normalizeHistoryRun = (raw: unknown): GbpAuditHistoryRun => {
	const obj = (raw ?? {}) as RawObject;
	const diff = (obj.diff ?? {}) as RawObject;
	return {
		runId: asString(obj.run_id),
		placeId: asString(obj.place_id),
		locationName: asString(obj.location_name),
		readOk: obj.read_ok === undefined ? true : Boolean(obj.read_ok),
		gbpLinked: obj.gbp_linked === undefined ? true : Boolean(obj.gbp_linked),
		worstSeverity: asString(obj.worst_severity) || "info",
		criticalCount: asNumber(obj.critical_count),
		warningCount: asNumber(obj.warning_count),
		infoCount: asNumber(obj.info_count),
		photoCount: asNumber(obj.photo_count),
		attributeCount: asNumber(obj.attribute_count),
		reviewSummary: (obj.review_summary ?? {}) as Record<string, unknown>,
		summary: asString(obj.summary),
		generatedAt: asString(obj.generated_at) || null,
		newCount: Array.isArray(diff.new) ? diff.new.length : 0,
		resolvedCount: Array.isArray(diff.resolved) ? diff.resolved.length : 0,
	};
};

const asNullableNumber = (value: unknown): number | null =>
	typeof value === "number" && Number.isFinite(value) ? value : null;

const normalizeDelta = (raw: unknown): GbpScanDelta => {
	const obj = (raw ?? {}) as RawObject;
	return {
		change: asNullableNumber(obj.change),
		improved: typeof obj.improved === "boolean" ? obj.improved : null,
	};
};

const normalizeScan = (raw: unknown): GbpScan => {
	const obj = (raw ?? {}) as RawObject;
	const deltas = (obj.deltas ?? {}) as RawObject;
	return {
		reportKey: asString(obj.report_key),
		keyword: asString(obj.keyword),
		gridSize: asNullableNumber(obj.grid_size),
		radius: asString(obj.radius),
		measurement: asString(obj.measurement),
		arp: asNullableNumber(obj.arp),
		atrp: asNullableNumber(obj.atrp),
		solv: asNullableNumber(obj.solv),
		foundIn: asNullableNumber(obj.found_in),
		dataPoints: asNullableNumber(obj.data_points),
		scannedAt: asString(obj.scanned_at) || null,
		deltas: {
			arp: normalizeDelta(deltas.arp),
			atrp: normalizeDelta(deltas.atrp),
			solv: normalizeDelta(deltas.solv),
		},
		comparedTo: asString(obj.compared_to) || null,
	};
};

/** A NAP field the drafter refused to use because Google contradicts it. */
export interface GbpWithheldField {
	field: string;
	/** Human wording, e.g. "phone number". */
	label: string;
	ours: string;
	theirs: string;
}

export interface GbpReplyDraft {
	reply: string;
	withheld: GbpWithheldField[];
}

export interface ReviewReplyParams {
	tenantId: string;
	reviewId: string;
	rating: number | null;
	comment: string;
	reviewer: string;
	tone?: "professional" | "warm" | "apologetic" | "brief";
}

export const gbpAuditService = {
	/**
	 * @param force skip the reuse window and re-read the profile. Free, but it
	 * writes another history row — so it is an explicit action, not a default.
	 */
	async get(
		tenantId: string,
		placeId = "",
		force = false,
		runId = "",
	): Promise<GbpAuditResult> {
		const params = new URLSearchParams({ tenant_id: tenantId });
		if (placeId) params.set("place_id", placeId);
		if (force) params.set("force", "true");
		// A stored run is re-rendered from what it recorded — no vendor call,
		// and no chance of today's values appearing beside old findings.
		if (runId) params.set("run_id", runId);
		const { data } = await apiClient.get(
			`/site-health/gbp-audit?${params.toString()}`,
			// A cold audit reads the whole review listing one vendor page at a
			// time — 803 reviews takes 23s. The client-wide 30s timeout cut
			// that off mid-read, so the page showed a network error while the
			// server quietly finished work nobody was waiting for any more
			// (Mike Hammer Moving, 2026-09-22). Runs are reused for hours, so
			// only the first viewer after a cache expiry waits at all.
			{ timeout: 120_000 },
		);
		return normalizeResult(data);
	},

	async history(
		tenantId: string,
		placeId = "",
		limit = 24,
	): Promise<GbpAuditHistoryRun[]> {
		const params = new URLSearchParams({
			tenant_id: tenantId,
			limit: String(limit),
		});
		if (placeId) params.set("place_id", placeId);
		const { data } = await apiClient.get(
			`/site-health/gbp-audit/history?${params.toString()}`,
		);
		const runs = (data as RawObject)?.runs;
		return Array.isArray(runs) ? runs.map(normalizeHistoryRun) : [];
	},

	/**
	 * Record the client's answer on one checklist item.
	 *
	 * `declined` suppresses the item from the failure counts and stops it
	 * being re-raised on later audits. Append-only on the backend, and the
	 * actor is taken from the session — deliberately NOT sent from here, so it
	 * cannot be spoofed.
	 */
	async decide(params: {
		tenantId: string;
		itemId: string;
		/** Must list every status the checklist can send. "flagged" was added
		 *  to the UI and not here, so it travelled as an untyped string —
		 *  harmless only because the backend happened to accept it. */
		status: "approved" | "declined" | "reopened" | "flagged";
		placeId?: string;
		note?: string;
	}): Promise<{ ok: boolean; error?: string }> {
		const { data } = await apiClient.post("/site-health/gbp-audit/decision", {
			tenant_id: params.tenantId,
			item_id: params.itemId,
			status: params.status,
			place_id: params.placeId ?? "",
			note: params.note ?? "",
		});
		const obj = (data ?? {}) as RawObject;
		// A refusal can arrive as 200 with ok:false — the caller MUST read
		// this, not just the absence of a thrown error.
		return { ok: Boolean(obj.ok), error: asString(obj.error) || undefined };
	},

	/**
	 * Past visibility scans and how the rank metrics moved. Free — listing
	 * reports never costs credits.
	 */
	async scanHistory(
		tenantId: string,
		placeId = "",
		limit = 24,
	): Promise<GbpScanHistory> {
		const params = new URLSearchParams({
			tenant_id: tenantId,
			limit: String(limit),
		});
		if (placeId) params.set("place_id", placeId);
		const { data } = await apiClient.get(
			`/site-health/gbp-scan-history?${params.toString()}`,
		);
		const obj = (data ?? {}) as RawObject;
		return {
			connected: Boolean(obj.connected),
			mapped: Boolean(obj.mapped),
			scans: Array.isArray(obj.scans) ? obj.scans.map(normalizeScan) : [],
			error: asString(obj.error) || null,
		};
	},

	/**
	 * Drafts a reply. NOTHING is posted to Google — the operator copies it.
	 * Returns "" when a draft could not be produced, and the operator writes
	 * their own, which is the behaviour without this feature at all.
	 */
	/**
	 * Publish an owner reply to a Google review. THIS GOES PUBLIC.
	 *
	 * Never throws on a refusal — the backend answers {ok:false, reason} so the
	 * operator is told why. `claims` lists assertions a person must vouch for;
	 * sending again with acknowledgedClaims true goes through.
	 *
	 * `posted` means Google ACCEPTED the reply, not that it is visible. Google
	 * moderates owner replies first — usually minutes, occasionally far longer.
	 */
	async postReply(params: PostReplyParams): Promise<GbpPostReplyResult> {
		const { data } = await apiClient.post(
			"/site-health/gbp-review-reply/post",
			{
				tenant_id: params.tenantId,
				review_id: params.reviewId,
				place_id: params.placeId ?? "",
				reply: params.reply,
				acknowledged_claims: params.acknowledgedClaims ?? false,
			},
		);
		const obj = (data ?? {}) as RawObject;
		return {
			ok: obj.ok === true,
			posted: obj.posted === true,
			reason: asString(obj.reason),
			claims: Array.isArray(obj.claims) ? obj.claims.map(asString) : [],
		};
	},

	async suggestReply(params: ReviewReplyParams): Promise<GbpReplyDraft> {
		const { data } = await apiClient.post("/site-health/gbp-review-reply", {
			tenant_id: params.tenantId,
			review_id: params.reviewId,
			rating: params.rating,
			comment: params.comment,
			reviewer: params.reviewer,
			tone: params.tone ?? "professional",
		});
		const obj = (data ?? {}) as RawObject;
		return {
			reply: asString(obj.reply),
			withheld: Array.isArray(obj.withheld)
				? obj.withheld.map((raw) => {
						const w = (raw ?? {}) as RawObject;
						return {
							field: asString(w.field),
							label: asString(w.label) || asString(w.field),
							ours: asString(w.ours),
							theirs: asString(w.theirs),
						};
					})
				: [],
		};
	},
};
